import React, { useState } from "react";
import {
  MdInfo,
  MdComputer,
  MdHome,
  MdVerified,
  MdOutlineWarning,
  MdOutlineCheckCircle,
  MdOutlineCancel,
  MdOutlineArrowDropDown,
  MdOutlineArrowDropUp,
  MdAccessTime,
  MdBookmark,
  MdCheck,
} from "react-icons/md";
import "./ExamGuidelines.css";

const ExamGuidelines = () => {
  const [activeSection, setActiveSection] = useState("before");
  const [expandedSections, setExpandedSections] = useState({
    before: null,
    during: null,
    post: null,
  });

  const handleSectionChange = (section) => {
    setActiveSection(section);
  };

  const toggleExpanded = (section, index) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: prev[section] === index ? null : index,
    }));
  };

  const beforeExamGuidelines = [
    {
      title: "System Requirements",
      items: [
        "Working webcam and microphone",
        "Stable internet connection (minimum 5 Mbps recommended)",
        "Supported browser (Google Chrome or Mozilla Firefox preferred)",
        "Disable any screen recording or remote access software",
      ],
      icon: <MdComputer />,
    },
    {
      title: "Environment Setup",
      items: [
        "Quiet, well-lit room with no background noise",
        "No one else present in the room",
        "Clear desk with only permitted items",
        "Proper lighting for clear face visibility",
      ],
      icon: <MdHome />,
    },
    {
      title: "Identity Verification",
      items: [
        "Upload institution-issued photo ID before exam starts",
        "Show valid government-issued or institution-issued photo ID to webcam",
        "Face the camera directly during ID verification",
        "Complete room scan during verification process",
      ],
      icon: <MdVerified />,
    },
  ];

  const duringExamGuidelines = [
    {
      title: "Proctoring Rules",
      items: [
        "Camera and microphone must remain on throughout the exam",
        "AI continuously monitors eye movements, noise, and suspicious activity",
        "Stay within camera frame at all times",
        "Do not leave your seat or look away frequently",
        "No mobile phones, smartwatches, or other electronic devices allowed",
        "No books, notes, or reference materials (unless explicitly permitted)",
        "No going back to previous questions after submission",
      ],
      icon: <MdInfo />,
    },
  ];

  const dosDonts = {
    dos: [
      "Read and understand all instructions before starting",
      "Ensure sufficient battery backup if using a laptop",
      "Maintain a stable internet connection throughout",
      "Read each question carefully before submitting",
      "Report technical issues immediately to support",
      "Keep alternative device/hotspot ready for connection failures",
    ],
    donts: [
      "Do not switch tabs or open other applications",
      "Do not talk, whisper, or seek help from anyone",
      "Do not use earphones, Bluetooth devices, or external screens",
      "Do not hide your face or obstruct webcam",
      "Do not use unauthorized materials, books, or devices",
      "Do not manipulate webcam or microphone settings",
      "Do not keep mobile phones, smartwatches, or books nearby",
      "Do not rush through questions as answers cannot be changed later",
    ],
  };

  const postExamGuidelines = [
    {
      title: "Post-Exam Guidelines",
      items: [
        "Submit exam properly before closing browser",
        "Ensure you've answered questions properly before proceeding",
        "Check that uploaded images are clear and readable",
        "Be aware the AI flags suspicious activity for review",
        "Results may be withheld if malpractice is detected",
      ],
      icon: <MdOutlineCheckCircle />,
    },
  ];

  // Tab content for rendering with icons
  const tabContent = {
    before: {
      icon: <MdAccessTime />,
      title: "Before Exam",
      subtitle: "Preparation & Requirements",
    },
    during: {
      icon: <MdBookmark />,
      title: "During Exam",
      subtitle: "Rules & Expectations",
    },
    post: {
      icon: <MdCheck />,
      title: "Post Exam",
      subtitle: "Submission & Follow-up",
    },
  };

  const renderGuidelineItem = (item, idx, section, sectionIndex) => {
    const isExpanded = expandedSections[section] === sectionIndex;

    return (
      <li key={idx} className={idx % 2 === 0 ? "even-item" : "odd-item"}>
        <span className="bullet">•</span>
        {item}
      </li>
    );
  };

  return (
    <div className="exam-guidelines">
      <div className="guidelines-header">
        <h3>Exam Guidelines</h3>
      </div>

      <div className="guidelines-tabs">
        {Object.entries(tabContent).map(([key, tab]) => (
          <button
            key={key}
            className={`tab-button ${activeSection === key ? "active" : ""}`}
            onClick={() => handleSectionChange(key)}
          >
            <span className="tab-icon">{tab.icon}</span>
            <div className="tab-text">
              <span className="tab-title">{tab.title}</span>
              <span className="tab-subtitle">{tab.subtitle}</span>
            </div>
          </button>
        ))}
      </div>

      <div className="guidelines-content">
        {activeSection === "before" && (
          <div className="before-exam-section">
            {beforeExamGuidelines.map((section, index) => (
              <div
                key={index}
                className={`guideline-section ${
                  expandedSections.before === index ? "expanded" : ""
                }`}
                onClick={() => toggleExpanded("before", index)}
              >
                <div className="section-header">
                  <div className="section-icon">{section.icon}</div>
                  <h4>{section.title}</h4>
                </div>
                <ul className="guideline-list">
                  {section.items.map((item, idx) =>
                    renderGuidelineItem(item, idx, "before", index)
                  )}
                </ul>
              </div>
            ))}
          </div>
        )}

        {activeSection === "during" && (
          <div className="during-exam-section">
            {duringExamGuidelines.map((section, index) => (
              <div
                key={index}
                className={`guideline-section ${
                  expandedSections.during === index ? "expanded" : ""
                }`}
                onClick={() => toggleExpanded("during", index)}
              >
                <div className="section-header">
                  <div className="section-icon">{section.icon}</div>
                  <h4>{section.title}</h4>
                </div>
                <ul className="guideline-list">
                  {section.items.map((item, idx) =>
                    renderGuidelineItem(item, idx, "during", index)
                  )}
                </ul>
              </div>
            ))}

            <div className="dos-donts-container">
              <div className="dos-section">
                <h5>
                  <MdOutlineCheckCircle className="heading-icon" /> Do's
                </h5>
                <ul className="guideline-list">
                  {dosDonts.dos.map((item, idx) => (
                    <li key={idx} className="do-item">
                      <span className="check-icon">✓</span>
                      <span className="item-text">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="donts-section">
                <h5>
                  <MdOutlineCancel className="heading-icon" /> Don'ts
                </h5>
                <ul className="guideline-list">
                  {dosDonts.donts.map((item, idx) => (
                    <li key={idx} className="dont-item">
                      <span className="x-icon">✗</span>
                      <span className="item-text">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {activeSection === "post" && (
          <div className="post-exam-section">
            {postExamGuidelines.map((section, index) => (
              <div
                key={index}
                className={`guideline-section ${
                  expandedSections.post === index ? "expanded" : ""
                }`}
                onClick={() => toggleExpanded("post", index)}
              >
                <div className="section-header">
                  <div className="section-icon">{section.icon}</div>
                  <h4>{section.title}</h4>
                </div>
                <ul className="guideline-list">
                  {section.items.map((item, idx) =>
                    renderGuidelineItem(item, idx, "post", index)
                  )}
                </ul>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ExamGuidelines;
